import { forwardRef, useEffect, useState } from 'react'

import { PhoneIcon } from '@nx/fire/assets'
import { SmartlingAttributes } from '@nx/smartling'

import {
  StyledCard,
  StyledContact,
  StyledContactGrid,
  StyledDescription,
  StyledEmailIcon,
  StyledEmailLink,
  StyledImage,
  StyledImageWrapper,
  StyledJobRole,
  StyledName,
  StyledPhoneLink,
  StyledTopWrapper,
} from './FirePersonCard.styles'
import { FirePersonCardProps } from './FirePersonCard.types'
import { ProfileLink } from './ProfileLink'

const DEFAULT_EMAIL_SUBJECT = 'New Enquiry'

export const FirePersonCard = forwardRef<HTMLDivElement, FirePersonCardProps>(
  (
    {
      name,
      jobRole,
      bio,
      nameTag = 'h2',
      imageUrl,
      personUrl,
      emailAddress,
      emailSubject = DEFAULT_EMAIL_SUBJECT,
      phoneNumber,
      isAlwaysCompact = false,
      ...props
    },
    ref
  ) => {
    const [finalEmailSubject, setFinalEmailSubject] = useState(emailSubject)
    const isContactOnlyCard = !!emailAddress || !!phoneNumber
    const profileImage = imageUrl ? `${imageUrl}&width=230` : ''

    useEffect(() => {
      if (
        typeof window !== 'undefined' &&
        emailSubject === DEFAULT_EMAIL_SUBJECT
      ) {
        const domain = window.location.hostname
        setFinalEmailSubject(`${emailSubject} from ${domain}`)
      }
    }, [emailSubject])

    return (
      <StyledCard
        {...SmartlingAttributes.noTranslate}
        $isAlwaysCompact={isAlwaysCompact}
        ref={ref}
        {...props}
      >
        <StyledTopWrapper
          $isAlwaysCompact={isAlwaysCompact}
          $isContactOnlyCard={isContactOnlyCard}
        >
          <StyledImageWrapper $isAlwaysCompact={isAlwaysCompact}>
            <StyledImage
              src={profileImage}
              width="115"
              height="115"
              alt={`Photo of ${name}`}
              placeholderPadding={false}
            />
          </StyledImageWrapper>
          <StyledName
            $level={6}
            as={nameTag}
            $isAlwaysCompact={isAlwaysCompact}
          >
            {name}
          </StyledName>
          {jobRole && <StyledJobRole $level="S">{jobRole}</StyledJobRole>}
        </StyledTopWrapper>
        {isContactOnlyCard ? (
          <div>
            {phoneNumber && (
              <StyledContactGrid $isAlwaysCompact={isAlwaysCompact}>
                <PhoneIcon width={18} height={18} />
                <StyledContact $level="S">
                  <StyledPhoneLink href={`tel:${phoneNumber}`}>
                    Tel: {phoneNumber}
                  </StyledPhoneLink>
                </StyledContact>
              </StyledContactGrid>
            )}
            {emailAddress && (
              <StyledContactGrid $isAlwaysCompact={isAlwaysCompact}>
                <StyledEmailIcon width={18} height={18} />
                <StyledContact $level="S">
                  <StyledEmailLink
                    href={`mailto:${emailAddress}?subject=${finalEmailSubject}`}
                  >
                    {emailAddress}
                  </StyledEmailLink>
                </StyledContact>
              </StyledContactGrid>
            )}
          </div>
        ) : (
          <>
            <div>
              <StyledDescription $level="S">{bio}</StyledDescription>
            </div>
            {personUrl && (
              <ProfileLink
                personUrl={personUrl}
                name={name}
                isAlwaysCompact={isAlwaysCompact}
              />
            )}
          </>
        )}
      </StyledCard>
    )
  }
)
